import React, { useContext, useState } from 'react'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonGroup,
  Typography,
} from '@mui/material'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import EventIcon from '@mui/icons-material/Event'
import moment from 'moment'
import { TenantSettingsContext, UserContext } from '../../App'
import API from '../../services/API'

// The applicant's post-submission view. Every milestone here reflects real
// pipeline data (references arriving, interviews being booked, offers going
// out) — the server derives it all automatically, nothing is aspirational.
const SubmissionTracker = ({ application, refresh }) => {
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const { user, setUser } = useContext(UserContext)
  const [responding, setResponding] = useState(false)

  const upcomingInterviews = (application.interviews ?? []).filter(
    (interview) => !interview.completed,
  )
  const interviewCompleted =
    (application.interviews ?? []).some((interview) => interview.completed) ||
    !!application.interview?.completedBy

  const offerOpen =
    application.offerExtended &&
    !application.offerAccepted &&
    !application.offerRejected

  const respondToOffer = (accept) => {
    setResponding(true)
    API.post('/my-application/offer-response', { accept })
      .then((res) => {
        if (res.status === 200) {
          refresh?.()
        }
      })
      .finally(() => setResponding(false))
  }

  const items = [
    {
      label: 'Application Submitted',
      isTrue: application.state !== 'Draft',
    },
    {
      label: 'Reference #1 Received',
      isTrue: !!application.reference1?.responseReceived,
      pendingHint: 'Waiting on your first referee to respond.',
    },
    {
      label: 'Reference #2 Received',
      isTrue: !!application.reference2?.responseReceived,
      pendingHint: 'Waiting on your second referee to respond.',
    },
    {
      label: 'Interview Scheduled',
      isTrue:
        upcomingInterviews.length > 0 ||
        interviewCompleted ||
        !!application.interview?.scheduled,
      detail:
        upcomingInterviews.length > 0
          ? upcomingInterviews
              .map(
                (interview) =>
                  `${interview.title || 'Interview'}: ${
                    interview.scheduledStart
                      ? moment(interview.scheduledStart).format(
                          'dddd Do MMMM YYYY [at] HH:mm',
                        )
                      : 'time to be confirmed'
                  }${interview.location ? ' — ' + interview.location : ''}`,
              )
              .join('\n')
          : null,
    },
    {
      label: 'Interview Completed',
      isTrue: interviewCompleted,
    },
    {
      label: 'Offer Extended',
      isTrue: !!application.offerExtended,
    },
    application.offerRejected
      ? {
          label: 'Offer Declined',
          isTrue: true,
          severity: 'warning',
        }
      : {
          label: 'Offer Accepted',
          isTrue: !!application.offerAccepted,
        },
    {
      label: 'Application Approved',
      isTrue: application.state === 'Approved',
    },
  ]

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="stretch"
      justifyContent="center"
    >
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        paddingX={2}
        paddingY={{ xs: 1, lg: 0 }}
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <Box display="flex" justifyContent="center">
          <Box
            component="img"
            src={orgSettings?.logo}
            padding={1}
            borderRadius={1}
            maxHeight="60px"
            maxWidth="100%"
          />
        </Box>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography>
            {user.firstName} {user.lastName}
          </Typography>
          <Avatar
            sx={{ width: 24, height: 24, borderRadius: 1 }}
            src={user.photograph}
          />
        </Stack>
      </Stack>
      <Stack
        component={Paper}
        gap={1}
        paddingY={{ xs: 1, lg: 3 }}
        paddingX={{ xs: 1, lg: 4 }}
      >
        <Typography variant="title2" textAlign="center">
          Your application has been submitted!
        </Typography>

        <Typography variant="title3" textAlign="center">
          Track the progress of your application below.
        </Typography>

        {upcomingInterviews.map((interview) => (
          <Alert
            key={interview._id}
            severity="info"
            icon={<EventIcon />}
            variant="outlined"
          >
            <Typography variant="body2" fontWeight={600}>
              {interview.title || 'Interview'} —{' '}
              {interview.scheduledStart
                ? moment(interview.scheduledStart).format(
                    'dddd Do MMMM YYYY [at] HH:mm',
                  )
                : 'time to be confirmed'}
            </Typography>
            {interview.location && (
              <Typography variant="body2">
                Location: {interview.location}
              </Typography>
            )}
            {interview.details && (
              <Typography variant="body2">{interview.details}</Typography>
            )}
          </Alert>
        ))}

        {offerOpen && (
          <Alert severity="success" variant="outlined">
            <Stack gap={1}>
              <Typography variant="body2" fontWeight={600}>
                You have been offered the role
                {application?.employee?.clinicalRole
                  ? ` of ${application.employee.clinicalRole}`
                  : ''}
                !
              </Typography>
              <Typography variant="body2">
                Please respond to the offer below.
              </Typography>
              <ButtonGroup disabled={responding}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => respondToOffer(true)}
                >
                  Accept Offer
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => respondToOffer(false)}
                >
                  Decline Offer
                </Button>
              </ButtonGroup>
            </Stack>
          </Alert>
        )}

        {items.map(({ label, isTrue, detail, pendingHint, severity }) => (
          <Alert
            variant={isTrue ? 'filled' : 'standard'}
            severity={isTrue ? (severity ?? 'success') : 'info'}
            key={label}
            icon={!isTrue ? <HourglassEmptyIcon /> : <CheckBoxIcon />}
            sx={{ whiteSpace: 'pre-line' }}
          >
            {label}
            {!isTrue && pendingHint ? ` — ${pendingHint}` : ''}
            {isTrue && detail ? `\n${detail}` : ''}
          </Alert>
        ))}
        <Button
          fullWidth
          onClick={() =>
            API.delete('/logout')
              .then(() => {
                setUser({})
              })
              .catch((e) => console.log(e))
          }
          variant="contained"
        >
          LOGOUT
        </Button>
      </Stack>
    </Box>
  )
}

export default SubmissionTracker
